import { useEffect, useRef } from 'react';
import type { IChartApi, Time } from 'lightweight-charts';

/** 有日记的一天（一天多篇合并计数），按日期升序 */
export interface NoteDay {
  date: string;
  count: number;
}

/** 归并到时间轴上的一个点（周/月/年K 上已并入所属周期的那根柱） */
export interface NoteDot {
  /** 落在时间轴上的时间点 */
  time: string;
  /** 悬停提示，如 "2026-09-29、2026-09-30 · 3 篇" */
  title: string;
  /** 是否为当前选中的那根柱 */
  selected: boolean;
  /**
   * 自定义圆点色（预测标记按情绪用 --up/--down/--flat，实心不透明）。
   * 缺省保持笔记原配色：普通点半透明蓝、选中柱高饱和蓝。
   */
  color?: string;
}

export const ROW_H = 14; // 每行浮动条高度：贴主图上边界内侧（可叠多行）
const DOT = 5;    // 圆点直径
const BAR_W = 3;  // 选中日竖条宽
const BAR_H = ROW_H - 4;
/** 视口外判定留的余量；圆点按 -50% 居中，不留会被 overflow 裁掉一半 */
const EDGE = 4;

/**
 * 图表上的「有日记」指示：不做任何带子、边框或占位，小圆点浮在主图上边界内侧。
 *
 * **为什么不用库的 createSeriesMarkers**：它的 belowBar 偏移是
 * `roundEven(barSpacing) × size`，没有可调参数 —— barSpacing=7、size=0.6 时
 * 离 K 线只有约 3.6px；且用主题 text1 作色（浅色主题近乎纯黑），是块突兀的黑方块。
 * 自绘 DOM 可以自由控制颜色、尺寸与位置。
 *
 * **为什么用 timeToCoordinate 定位**：按「时间点」换算横坐标。
 * 但注意它对时间轴上**不存在**的点返回 null —— 周/月/年柱的时间点只有
 * 周期最后交易日，而日记记在任意交易日，所以入参必须是已归并到柱的
 * 周期日期（见 ChartCanvas 的 laneNotes），否则圆点会整个消失。
 *
 * 平移/缩放期间只写 transform，不走 setState —— 否则每次拖动都会整块重渲染。
 */
export function NoteLane({
  chart,
  notes,
  /** 该行的行号：0=贴顶（笔记），1=第二行（预测），依此类推 */
  row = 0,
}: {
  chart: IChartApi | null;
  notes: NoteDot[];
  row?: number;
}) {
  const laneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const lane = laneRef.current;
    if (!lane || !chart) return;
    const ts = chart.timeScale();
    const place = () => {
      const w = lane.clientWidth;
      for (const el of lane.children) {
        const node = el as HTMLElement;
        const x = ts.timeToCoordinate(node.dataset.date as Time);
        // 非交易日等库里排不进去的时间返回 null
        node.style.visibility = x !== null && x >= EDGE && x <= w - EDGE ? 'visible' : 'hidden';
        if (x !== null) node.style.transform = `translate(-50%,-50%) translateX(${x}px)`;
      }
    };
    place();
    ts.subscribeVisibleLogicalRangeChange(place);
    const ro = new ResizeObserver(place);
    ro.observe(lane);
    return () => {
      ts.unsubscribeVisibleLogicalRangeChange(place);
      ro.disconnect();
    };
  }, [chart, notes]);

  if (notes.length === 0) return null;

  return (
    <div
      ref={laneRef}
      aria-hidden
      style={{
        position: 'absolute',
        top: row * ROW_H,
        left: 0,
        right: 0,
        height: ROW_H,
        overflow: 'hidden',
        // 必须高于图表自身的 canvas：库给它们加了 z-index 1/2，
        // 不显式给层级这一层会被整块压在下面（elementFromPoint 能看到被遮）。
        zIndex: 3,
        // 整层不挡图表：十字光标与点柱选日要照常穿透到下方 canvas；
        // 只让圆点自身可悬停以显示 title
        pointerEvents: 'none',
      }}
    >
      {notes.map((n) => (
        <span
          key={n.time}
          data-date={n.time}
          title={n.title}
          style={{
            position: 'absolute',
            left: 0,
            top: ROW_H / 2,
            width: n.selected ? BAR_W : DOT,
            height: n.selected ? BAR_H : DOT,
            borderRadius: 2,
            // 预测标记传了自己的情绪色（实心）；笔记维持原双色方案，
            // 选中柱换高饱和色 —— 选中反馈优先于普通形态
            background: n.color ?? (n.selected ? 'var(--note-dot-strong)' : 'var(--note-dot)'),
            pointerEvents: 'auto',
          }}
        />
      ))}
    </div>
  );
}